import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

const AcceptInvitationPage = ({ handleLogin, setCurrentPage }) => {
  const [searchParams] = useSearchParams();
  
  // Extract token from various possible locations
  const [token, setToken] = useState(() => {
    // 1. Get token from query param
    const queryToken = searchParams.get('token');
    
    // 2. Get token from path if it's in /invitation/{token} format
    const pathToken = window.location.pathname.startsWith('/invitation/') 
      ? window.location.pathname.split('/')[2] 
      : null;
      
    // 3. Get token from hash fragment (for hash-based routing)
    // Extract token from hash-based URL like /#/accept-invitation?token=xyz123
    let hashToken = null;
    const hash = window.location.hash.substring(1); // Remove the '#' character
    if (hash) {
      const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';
      const hashParams = new URLSearchParams(hashQuery);
      hashToken = hashParams.get('token');
    }
    
    // Use token from any of the possible sources (priority: query, path, hash)
    return queryToken || pathToken || hashToken;
  });

  // Add an effect to handle token extraction from hash
  useEffect(() => {
    // If we don't have a token yet, check the hash
    if (!token) {
      const hash = window.location.hash.substring(1); // Remove the '#' character
      if (hash) {
        const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';
        const hashParams = new URLSearchParams(hashQuery);
        const hashToken = hashParams.get('token');
        
        if (hashToken) {
          // If we found a token in the hash, trigger verification
          setToken(hashToken);
        }
      }
    }
  }, [token]);

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });

  const verifyInvitation = useCallback(async () => {
    try {
      setLoading(true);
      
      // Clean up the token - ensure it doesn't contain path segments
      const cleanToken = token.replace(/^\/invitation\//, '')  // Remove leading /invitation/ if present
                             .replace(/^invitation\//, '');     // Remove leading invitation/ if present
      
      // Use the correct API endpoint format: GET /invitation/{token}
      const response = await api.get(`/invitation/${cleanToken}`);
      
      if (response.data.valid) {
        // Store the entire invitation data object
        setInvitation(response.data);
        // Pre-fill email if specified in the invitation
        if (response.data.email) {
          setFormData(prev => ({ ...prev, email: response.data.email }));
        }
      } else {
        setError(response.data.message || 'Invalid invitation');
      }
    } catch (error) {
      console.error('Error verifying invitation:', error);
      setError('Error verifying invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    verifyInvitation();
  }, [token, verifyInvitation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create user with invitation
      const userData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.name,
        plan_id: invitation?.plan_id || 0  // Use the plan ID from the invitation
      };

      console.log('Redeeming invitation with token:', token);
      
      // Clean up the token - ensure it doesn't contain path segments
      const cleanToken = token.replace(/^\/invitation\//, '')  // Remove leading /invitation/ if present
                             .replace(/^invitation\//, '');     // Remove leading invitation/ if present
      
      const registrationResponse = await api.post('/redeem-invitation', {
        token: cleanToken,
        ...userData
      });

      // If email verification is bypassed, login immediately
      // Otherwise, show a success message about checking email
      if (registrationResponse.data.message.includes("already activated")) {
        // Login the user immediately if verification was bypassed
        const loginResult = await handleLogin(formData.email, formData.password);
        
        if (!loginResult.success) {
          throw new Error(loginResult.message || 'Login failed after registration');
        }
        
        // Navigate to app
        setCurrentPage('app');
      } else {
        // Show message about email verification
        // Store success message in localStorage before redirecting
        localStorage.setItem('auth_message', registrationResponse.data.message);
        setCurrentPage('auth');
        return { success: true, message: registrationResponse.data.message };
      }
    } catch (error) {
      console.error('Error redeeming invitation:', error);
      setError(error.response?.data?.detail || 'Error creating account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 min-h-[70vh]">
        <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-md">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Invitation Error</h2>
            <p className="text-red-600">{error}</p>
          </div>
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setCurrentPage('home')}
              className="text-emerald-600 hover:text-emerald-500"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center py-12 px-4 sm:px-6 lg:px-8 min-h-[70vh]">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            You've Been Invited!
          </h2>
          <p className="text-gray-600 mb-2">
            You've been invited to join MenuAnalyzer.AI with the
          </p>
          <p className="text-lg font-semibold text-emerald-600 mb-2">
            {invitation?.plan_name}
          </p>
          <p className="text-sm text-gray-500">
            This invitation expires on{' '}
            {new Date(invitation?.expires_at).toLocaleDateString()}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                readOnly={invitation?.email !== null}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 ${
                  invitation?.email ? 'bg-gray-100' : ''
                }`}
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
              />
              {invitation?.email && (
                <p className="mt-1 text-xs text-gray-500">
                  This invitation is specifically for this email address
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Create account & Accept invitation'
              )}
            </button>
          </div>
        </form>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => setCurrentPage('auth')}
            className="font-medium text-emerald-600 hover:text-emerald-500"
          >
            Go to login
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
